import type {
  Exam,
  OfficialExamResult,
  OfficialExamResultDetail,
  ExamQuestion,
  ExamWithQuestions,
  ExamSummary,
  ExamAttemptState,
  ExamResults,
  MyExamAttempt,
} from '@proref/types';
import type {
  ReportProctoringInput,
  StartPracticeTestInput,
  CreateExamInput,
  UpdateExamInput,
  CreateExamQuestionInput,
  UpdateExamQuestionInput,
  AddExamClipQuestionsInput,
  AddExamRuleQuestionsInput,
  SaveExamAnswerInput,
} from '@proref/schemas';
import { api, unwrap, type Envelope } from '../lib/api';

export interface ListExamsParams {
  // super_admin console filters; ignored server-side for a federation_admin (already pinned).
  sportId?: string;
  federationId?: string;
}

// The exam list — role-branched server-side (referee → published own-fed+sport; admin → drafts
// too; super_admin → all). Items carry question_count; no questions/answers in this payload.
export async function listExams(params: ListExamsParams = {}): Promise<ExamSummary[]> {
  const query: Record<string, string> = {};
  if (params.sportId) query['sport_id'] = params.sportId;
  if (params.federationId) query['federation_id'] = params.federationId;
  const res = await api.get<Envelope<ExamSummary[]>>('/exams', {
    params: Object.keys(query).length > 0 ? query : undefined,
  });
  return unwrap(res.data);
}

// One exam with its ordered questions (admin read — questions carry correct_answer).
export async function getExam(id: string): Promise<ExamWithQuestions> {
  const res = await api.get<Envelope<ExamWithQuestions>>(`/exams/${id}`);
  return unwrap(res.data);
}

export async function createExam(input: CreateExamInput): Promise<Exam> {
  const res = await api.post<Envelope<Exam>>('/exams', input);
  return unwrap(res.data);
}

export async function updateExam(id: string, input: UpdateExamInput): Promise<Exam> {
  const res = await api.patch<Envelope<Exam>>(`/exams/${id}`, input);
  return unwrap(res.data);
}

// Refused (409 EXAM_PUBLISHED) while the exam is published — the caller unpublishes first.
export async function deleteExam(id: string): Promise<void> {
  await api.delete<Envelope<null>>(`/exams/${id}`);
}

// Publish → live for referees. Gated server-side on having >=1 question (400 EXAM_NO_QUESTIONS).
export async function publishExam(id: string): Promise<Exam> {
  const res = await api.post<Envelope<Exam>>(`/exams/${id}/publish`);
  return unwrap(res.data);
}

export async function unpublishExam(id: string): Promise<Exam> {
  const res = await api.delete<Envelope<Exam>>(`/exams/${id}/publish`);
  return unwrap(res.data);
}

// ── Questions ──────────────────────────────────────────────────────────────

export async function addExamQuestion(examId: string, input: CreateExamQuestionInput): Promise<ExamQuestion> {
  const res = await api.post<Envelope<ExamQuestion>>(`/exams/${examId}/questions`, input);
  return unwrap(res.data);
}

// Add several clip questions at once — the "build an exam out of selected videos" flow. Returns
// the questions actually created (clips already on the exam are skipped server-side, so the
// array may be shorter than clip_ids).
export async function addExamClipQuestions(examId: string, input: AddExamClipQuestionsInput): Promise<ExamQuestion[]> {
  const res = await api.post<Envelope<ExamQuestion[]>>(`/exams/${examId}/questions/batch`, input);
  return unwrap(res.data);
}

// Add several rule-test questions at once — the "stage from the library, save once" flow. One
// atomic request, so there's no per-add delay and the exam is only re-synced once.
export async function addExamRuleQuestions(examId: string, input: AddExamRuleQuestionsInput): Promise<ExamQuestion[]> {
  const res = await api.post<Envelope<ExamQuestion[]>>(`/exams/${examId}/questions/rule-batch`, input);
  return unwrap(res.data);
}

export async function updateExamQuestion(id: string, input: UpdateExamQuestionInput): Promise<ExamQuestion> {
  const res = await api.patch<Envelope<ExamQuestion>>(`/exam-questions/${id}`, input);
  return unwrap(res.data);
}

export async function deleteExamQuestion(id: string): Promise<void> {
  await api.delete<Envelope<null>>(`/exam-questions/${id}`);
}

// ── Taking (referee) ─────────────────────────────────────────────────────────

// The caller's own attempts, newest first — resume banner (completed_at null) + recent results.
export async function listMyAttempts(): Promise<MyExamAttempt[]> {
  const res = await api.get<Envelope<MyExamAttempt[]>>('/exam-attempts');
  return unwrap(res.data);
}

// Start a fresh attempt → the taker view (questions without answers + the deadline).
// password is only needed for an Official (proctored) exam; omitted for a normal one.
export async function startExamAttempt(examId: string, password?: string): Promise<ExamAttemptState> {
  const res = await api.post<Envelope<ExamAttemptState>>(
    `/exams/${examId}/attempts`,
    password ? { password } : {},
  );
  return unwrap(res.data);
}

// Start a generated practice test — N random questions from the bank, optionally timed. Returns
// the same taker view as starting an authored exam, so the runner needs no special case.
export async function startPracticeTest(input: StartPracticeTestInput): Promise<ExamAttemptState> {
  const res = await api.post<Envelope<ExamAttemptState>>('/practice-tests', input);
  return unwrap(res.data);
}

// Resume — the current state of an attempt (questions + saved answers + expired flag).
export async function getExamAttempt(attemptId: string): Promise<ExamAttemptState> {
  const res = await api.get<Envelope<ExamAttemptState>>(`/exam-attempts/${attemptId}`);
  return unwrap(res.data);
}

// Autosave one answer (upsert). 409 past the deadline / after submit — surfaced via the envelope.
export async function saveExamAnswer(attemptId: string, input: SaveExamAnswerInput): Promise<void> {
  await api.patch<Envelope<null>>(`/exam-attempts/${attemptId}/answers`, input);
}

// Report the running proctoring totals for an in-progress Official exam. Fire-and-forget: the
// server takes the max of stored and reported, so a dropped request costs nothing.
export async function reportProctoring(attemptId: string, input: ReportProctoringInput): Promise<void> {
  await api.post<Envelope<null>>(`/exam-attempts/${attemptId}/proctoring`, input);
}

// Submit → grade + return the results (idempotent server-side).
export async function submitExamAttempt(attemptId: string): Promise<ExamResults> {
  const res = await api.post<Envelope<ExamResults>>(`/exam-attempts/${attemptId}/submit`);
  return unwrap(res.data);
}

// Post-submit results (409 before submit).
export async function getExamResults(attemptId: string): Promise<ExamResults> {
  const res = await api.get<Envelope<ExamResults>>(`/exam-attempts/${attemptId}/results`);
  return unwrap(res.data);
}

// Abandon an in-progress attempt — deletes it so it drops off the resume banner (409 if already
// submitted).
export async function abandonExamAttempt(attemptId: string): Promise<void> {
  await api.delete<Envelope<null>>(`/exam-attempts/${attemptId}`);
}

// ── Official results (admin) ─────────────────────────────────────────────────

// Every completed attempt at one exam — the federation admin's cohort table.
export async function listExamResults(examId: string): Promise<OfficialExamResult[]> {
  const res = await api.get<Envelope<OfficialExamResult[]>>(`/exams/${examId}/results`);
  return unwrap(res.data);
}

// One candidate's sitting: identity, proctoring tallies and the per-question breakdown. Admin
// only, and readable even while the breakdown is still withheld from candidates.
export async function getExamResultDetail(examId: string, attemptId: string): Promise<OfficialExamResultDetail> {
  const res = await api.get<Envelope<OfficialExamResultDetail>>(`/exams/${examId}/results/${attemptId}`);
  return unwrap(res.data);
}

// Release (or take back) the per-question breakdown for this exam's candidates.
export async function setExamResultsReleased(examId: string, released: boolean): Promise<Exam> {
  const res = released
    ? await api.post<Envelope<Exam>>(`/exams/${examId}/results/release`)
    : await api.delete<Envelope<Exam>>(`/exams/${examId}/results/release`);
  return unwrap(res.data);
}

// Delete one candidate's result — for a sitting invalidated by a technical fault. Unrecoverable.
export async function deleteExamResult(examId: string, attemptId: string): Promise<void> {
  await api.delete<Envelope<null>>(`/exams/${examId}/results/${attemptId}`);
}

// Download the cohort results as CSV. The response is a file, not the usual envelope, so it's
// fetched as a blob and handed to the browser as a download.
export async function downloadExamResultsCsv(examId: string, filename: string): Promise<void> {
  const res = await api.get(`/exams/${examId}/results/export`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  // Revoking immediately would race the download on some browsers; a tick is enough for the
  // click to have been handed off.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
